/**
 * Administration landing — workspace and (if permitted) platform surfaces.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, Settings, Users, KeyRound, ScrollText, ExternalLink, ArrowRight,
} from "lucide-react";
import { apiGet } from "@/lib/api";

const WORKSPACE_CARDS = [
  { to: "/v3/settings",             label: "Workspace Settings",
    desc: "Branding, integrations, notification preferences.",
    icon: Settings, accent: "#3B82F6",
    testid: "v3-admin-card-settings" },
  { to: "/v3/business/workspace",   label: "Seats & Team",
    desc: "Invite teammates, assign roles, manage seats.",
    icon: Users, accent: "#10B981",
    testid: "v3-admin-card-seats" },
  { to: "/v3/business/billing",     label: "Billing",
    desc: "Plan, invoices and usage.",
    icon: KeyRound, accent: "#F59E0B",
    testid: "v3-admin-card-billing" },
  { to: "/v3/admin/console",        label: "Admin Console",
    desc: "Users, revenue, valuation, health, feature flags, and everything else.",
    icon: Shield, accent: "#0B132B",
    testid: "v3-admin-card-console" },
];

const PLATFORM_CARDS = [
  { to: "/platform",           label: "Platform Console",
    desc: "Tenants, revenue, feature flags, audit log.",
    icon: Shield, accent: "#8B5CF6",
    testid: "v3-admin-card-platform" },
  { to: "/platform/audit",     label: "Audit Log",
    desc: "Immutable timeline of every platform action.",
    icon: ScrollText, accent: "#EF4444",
    testid: "v3-admin-card-audit" },
  { to: "/platform/waitlist",  label: "Launch Waitlist",
    desc: "Pre-launch signups, referrals and CSV export.",
    icon: Users, accent: "#10B981",
    testid: "v3-admin-card-waitlist" },
];

export default function AdminLanding() {
  const [hasPlatform, setHasPlatform] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const me = await apiGet("/v3/platform/me");
        setHasPlatform(Array.isArray(me?.platform_roles)
          && me.platform_roles.length > 0);
      } catch { /* no platform role */ }
    })();
  }, []);

  return (
    <div data-testid="v3-admin-landing" className="max-w-6xl">
      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">
        Administration
      </div>
      <h1 className="mt-2 text-[32px] md:text-[38px] font-bold text-slate-900 tracking-tight leading-tight">
        Administration
      </h1>
      <p className="mt-3 text-slate-600 max-w-2xl text-[15px] leading-relaxed">
        Workspace controls and — if you have the role — platform-level operations.
      </p>

      <Section title="Workspace" testid="v3-admin-workspace-section">
        {WORKSPACE_CARDS.map((c) => <Card key={c.to} {...c} />)}
      </Section>

      {hasPlatform && (
        <Section title="Platform" testid="v3-admin-platform-section">
          {PLATFORM_CARDS.map((c) => <Card key={c.to} {...c} />)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="mt-10" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
        {title}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

function Card({ to, label, desc, icon: Icon, external, accent, testid }) {
  return (
    <Link
      to={to} data-testid={testid}
      className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-[0.10] pointer-events-none"
           style={{ backgroundColor: accent }} />
      <div className="relative flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center text-white shadow-md shrink-0"
             style={{ backgroundImage: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-slate-900 inline-flex items-center gap-1">
            {label}
            {external && <ExternalLink className="w-3 h-3 text-slate-400" />}
          </div>
          <div className="mt-1 text-[12px] text-slate-500 leading-relaxed">
            {desc}
          </div>
        </div>
      </div>
      <div className="mt-5 text-[12px] font-semibold inline-flex items-center gap-1"
           style={{ color: accent }}>
        Open <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
